import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { CustomerSelect } from '@/components/CustomerSelect';
import { FormGroup } from '@/components/FormGroup';
import { PasswordInput } from '@/components/PasswordInput';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useCreateUser } from '@/hooks/useCreateUser';
import { RoleEnum, StatusEnum } from '@/services/UserService';

const userSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  role: z.nativeEnum(RoleEnum),
  status: z.nativeEnum(StatusEnum),
  customer: z.string().min(1, 'Customer is required'),
});

type UserFormData = z.infer<typeof userSchema>;

interface FormProps {
  onSuccess: (value: boolean) => void;
}

export default function Form({ onSuccess }: FormProps) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      role: RoleEnum.User,
      status: StatusEnum.Active,
    },
  });

  const { createUser, isLoading } = useCreateUser();

  const handleSetPassword = useCallback(
    (value: string) => {
      setValue('password', value);
    },
    [setValue],
  );

  const onSubmit = handleSubmit(async (data: UserFormData) => {
    try {
      await createUser({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        role: { id: data.role },
        status: { id: data.status },
        customer: { id: parseInt(data.customer, 10) },
      });
      onSuccess(true);
      reset();
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 422) {
        toast.error('Email already exists. Please try again.');
        return;
      }
      toast.error('Failed to create user. Please try again.');
    }
  });

  return (
    <div style={{ minHeight: '100vh' }}>
      <Card className="mx-auto w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Create New User</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <FormGroup
              error={{ message: errors.firstName?.message, font: 'sm' }}
            >
              <RequiredLabel htmlFor="firstName">First Name</RequiredLabel>
              <Input id="firstName" {...register('firstName')} />
            </FormGroup>

            <FormGroup
              error={{ message: errors.lastName?.message, font: 'sm' }}
            >
              <RequiredLabel htmlFor="lastName">Last Name</RequiredLabel>
              <Input id="lastName" {...register('lastName')} />
            </FormGroup>

            <FormGroup error={{ message: errors.email?.message, font: 'sm' }}>
              <RequiredLabel htmlFor="email">Email</RequiredLabel>
              <Input id="email" type="email" {...register('email')} />
            </FormGroup>

            <FormGroup
              error={{ message: errors.password?.message, font: 'sm' }}
            >
              <RequiredLabel htmlFor="password">Password</RequiredLabel>

              <PasswordInput
                id="password"
                {...register('password')}
                generatorButton={{
                  enable: true,
                  onGenerate: handleSetPassword,
                }}
              />
            </FormGroup>

            <FormGroup error={{ message: errors.role?.message, font: 'sm' }}>
              <RequiredLabel htmlFor="role">Role</RequiredLabel>
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    defaultValue={field.value.toString()}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={RoleEnum.User.toString()}>
                        User
                      </SelectItem>
                      <SelectItem value={RoleEnum.Admin.toString()}>
                        Admin
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </FormGroup>

            <FormGroup error={{ message: errors.status?.message, font: 'sm' }}>
              <RequiredLabel htmlFor="status">Status</RequiredLabel>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    defaultValue={field.value.toString()}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={StatusEnum.Active.toString()}>
                        Active
                      </SelectItem>
                      <SelectItem value={StatusEnum.Inactive.toString()}>
                        Inactive
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </FormGroup>

            <FormGroup
              error={{ message: errors.customer?.message, font: 'sm' }}
            >
              <RequiredLabel htmlFor="customer">Customer</RequiredLabel>
              <Controller
                name="customer"
                control={control}
                render={({ field }) => (
                  <CustomerSelect
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  />
                )}
              />
            </FormGroup>

            <p className="mt-4 text-sm text-gray-500">
              <span className="text-red-500">*</span> Required field
            </p>
            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting || isLoading}
            >
              {(isSubmitting || isLoading) && (
                <Loader2 className="animate-spin" />
              )}

              {!isSubmitting && !isLoading && 'Create User'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
